import { HTMLAttributes, MouseEvent, RefObject, forwardRef } from 'react';

import { LoadMoreButton, LoadMoreRow } from './styles';
import { OptionsPaging } from './types';

export interface OptionsListboxProps extends HTMLAttributes<HTMLUListElement> {
  pagingRef: RefObject<OptionsPaging>;
  pageSize: number;
  onShowMore: () => void;
}

// Listbox that appends a "show more" footer so a long option list can be
// browsed a page at a time instead of being silently truncated.
// `pagingRef` is read while rendering on purpose: the counters are written by
// `filterOptions`, which MUI runs right before it renders this listbox.
const OptionsListbox = forwardRef<HTMLUListElement, OptionsListboxProps>(
  ({ children, pagingRef, pageSize, onShowMore, ...listboxProps }, ref) => {
    const { total, shown } = pagingRef.current || { total: 0, shown: 0 };
    const remaining = total - shown;

    const handleShowMoreClick = (event: MouseEvent<HTMLButtonElement>) => {
      // Keeps the click from being treated as an option selection.
      event.preventDefault();
      event.stopPropagation();
      onShowMore();
    };

    return (
      <ul ref={ref} {...listboxProps}>
        {children}
        {remaining > 0 && (
          <LoadMoreRow role="presentation">
            <span>{`Showing ${shown} of ${total}`}</span>
            <LoadMoreButton type="button" onClick={handleShowMoreClick}>
              {`Show ${Math.min(pageSize, remaining)} more`}
            </LoadMoreButton>
          </LoadMoreRow>
        )}
      </ul>
    );
  },
);

OptionsListbox.displayName = 'OptionsListbox';

export default OptionsListbox;
